import type { NamedModalHost } from '@highereducation/hen-ui';

const MODAL_OPEN = 'modal.open';
const MODAL_CLOSE = 'modal.close';

/**
 * Open or close a named hen.modal. Blank `params.name` and a name with no
 * live registration warn and no-op, for both open and close.
 *
 * `modal.open` may also carry `params.index`, e.g. `'{{event}}'` from a
 * gallery's On image activation, and `params.tab`, a Tab ID that a Tabs in
 * the popup opens on. They reach the Modal content as `{ index, tab }`
 * through `useModalOpenPayload`. Blank values are left out, and with neither
 * the popup opens plainly.
 */
export function dispatchNamedModalAction(
  host: NamedModalHost,
  type: string,
  params?: Readonly<Record<string, unknown>>,
  source = 'Modal',
): void {
  if (type !== MODAL_OPEN && type !== MODAL_CLOSE) {
    return;
  }

  const name = params?.name;
  if (typeof name !== 'string' || name.trim().length === 0) {
    // eslint-disable-next-line no-console
    console.warn(
      `[${source}] ${type} ignored — params.name must be a non-empty string.`,
    );
    return;
  }

  const normalizedName = name.trim();
  const payload = type === MODAL_OPEN ? readOpenPayload(params) : undefined;
  const handled =
    type === MODAL_CLOSE
      ? host.close(normalizedName)
      : payload === undefined
        ? host.open(normalizedName)
        : host.open(normalizedName, payload);
  if (!handled) {
    // eslint-disable-next-line no-console
    console.warn(
      `[${source}] ${type} ignored — no Modal registered as "${normalizedName}".`,
    );
  }
}

function readOpenPayload(
  params: Readonly<Record<string, unknown>> | undefined,
): { index?: number; tab?: string } | undefined {
  const index = readIndex(params?.index);
  const tab =
    typeof params?.tab === 'string' && params.tab.trim() !== ''
      ? params.tab.trim()
      : undefined;
  if (index === undefined && tab === undefined) return undefined;
  return {
    ...(index === undefined ? {} : { index }),
    ...(tab === undefined ? {} : { tab }),
  };
}

/** Interpolated params can arrive as text, so `"3"` is accepted as 3. */
function readIndex(value: unknown): number | undefined {
  const parsed =
    typeof value === 'number'
      ? value
      : typeof value === 'string' && value.trim() !== ''
        ? Number(value)
        : Number.NaN;
  return Number.isFinite(parsed) && parsed >= 0
    ? Math.floor(parsed)
    : undefined;
}
